import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import api from '../lib/api';
import { Building2, MapPin, Cog, Users, Wrench, CheckCircle, ChevronRight, ChevronLeft, BookOpen, ArrowRight, Play, Pause, BarChart3, Activity, Camera, TrendingDown, DollarSign } from 'lucide-react';

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to DigSense IQ',
    subtitle: 'Track life. Compare cost. Cut waste.',
    icon: BookOpen,
    visual: 'welcome',
    duration: 5000,
    highlights: ['Real-time cost tracking', 'AI-powered recommendations', 'Executive-ready reports'],
  },
  {
    id: 'setup',
    title: 'Set Up Your Organization',
    subtitle: 'Companies, Sites & Machines',
    icon: Building2,
    visual: 'setup',
    duration: 6000,
    action: '/companies',
    actionLabel: 'Go to Setup',
    highlights: ['Create your company profile', 'Add mining sites with location & strata', 'Register each excavator by serial number'],
  },
  {
    id: 'team',
    title: 'Invite Your Team',
    subtitle: 'Role-based access for everyone',
    icon: Users,
    visual: 'team',
    duration: 5000,
    action: '/users',
    actionLabel: 'Manage Users',
    highlights: ['Company Admins see everything', 'Site Admins manage their location', 'Site Users record change-outs in the field'],
  },
  {
    id: 'changeout',
    title: 'Record a Change-Out',
    subtitle: 'Your core daily workflow',
    icon: Wrench,
    visual: 'changeout',
    duration: 7000,
    action: '/change-out',
    actionLabel: 'Try It Now',
    highlights: ['Select machine & read hour meter', 'Pick products — supplier auto-fills', 'Quick Repeat reuses last config', 'Attach proof photos for compliance'],
  },
  {
    id: 'analytics',
    title: 'Unlock Cost Intelligence',
    subtitle: 'Data-driven decisions',
    icon: BarChart3,
    visual: 'analytics',
    duration: 6000,
    action: '/analytics',
    actionLabel: 'View Analytics',
    highlights: ['Compare products by cost/hour', 'Find hidden savings opportunities', 'Fleet status with health indicators', 'Export PDF & Excel reports'],
  },
  {
    id: 'finish',
    title: 'You\'re Ready!',
    subtitle: 'Start saving on every tooth point',
    icon: CheckCircle,
    visual: 'finish',
    duration: 4000,
    highlights: ['Your dashboard auto-populates as you log data', 'AI savings agent finds cost leaks', 'Fleet alerts when machines need attention'],
  },
];

// Animated visual components for each step
function WelcomeVisual({ active }) {
  return (
    <div className="relative h-full flex flex-col items-center justify-center overflow-hidden">
      <div className={`transition-all duration-700 ${active ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center mx-auto mb-3 shadow-xl">
            <Activity size={28} className="text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">DigSense IQ</h3>
          <p className="text-xs text-slate-400 mt-1">Track life. Compare cost. Cut waste.</p>
        </div>
      </div>
      {/* Animated stat cards */}
      <div className="flex gap-3">
        {[
          { label: 'Cost/Hr', value: '₹137', color: 'bg-emerald-500', delay: '0.3s' },
          { label: 'Savings', value: '₹43L', color: 'bg-amber-500', delay: '0.6s' },
          { label: 'Machines', value: '21', color: 'bg-blue-500', delay: '0.9s' },
        ].map((s, i) => (
          <div key={i} className={`rounded-lg px-4 py-3 bg-white shadow-md border border-slate-100 text-center transition-all duration-500 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: s.delay }}>
            <div className={`w-2 h-2 rounded-full ${s.color} mx-auto mb-1`} />
            <p className="text-lg font-bold text-slate-900">{s.value}</p>
            <p className="text-[10px] text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SetupVisual({ active }) {
  const items = [
    { icon: Building2, label: 'Company', sub: 'Titan Mining', delay: '0.2s' },
    { icon: MapPin, label: 'Sites', sub: '8 locations', delay: '0.5s' },
    { icon: Cog, label: 'Machines', sub: '21 excavators', delay: '0.8s' },
  ];
  return (
    <div className="h-full flex items-center justify-center">
      <div className="space-y-3 w-full max-w-[260px]">
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} className={`flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm border border-slate-100 transition-all duration-500 ${active ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: item.delay }}>
              <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
                <Icon size={18} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                <p className="text-xs text-slate-400">{item.sub}</p>
              </div>
              <CheckCircle size={16} className={`ml-auto text-emerald-500 transition-all duration-300 ${active ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`} style={{ transitionDelay: `${parseFloat(item.delay) + 0.3}s` }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamVisual({ active }) {
  const roles = [
    { name: 'Admin', color: 'bg-slate-900', ring: 'ring-slate-200', delay: '0.2s' },
    { name: 'Site Mgr', color: 'bg-blue-600', ring: 'ring-blue-100', delay: '0.5s' },
    { name: 'Operator', color: 'bg-emerald-600', ring: 'ring-emerald-100', delay: '0.8s' },
  ];
  return (
    <div className="h-full flex items-center justify-center">
      <div className="flex items-end gap-5">
        {roles.map((r, i) => (
          <div key={i} className={`flex flex-col items-center transition-all duration-500 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: r.delay }}>
            <div className={`w-14 h-14 rounded-full ${r.color} flex items-center justify-center ring-4 ${r.ring} shadow-lg mb-2`}>
              <Users size={20} className="text-white" />
            </div>
            <span className="text-xs font-semibold text-slate-700">{r.name}</span>
            <div className={`mt-2 h-1 rounded-full transition-all duration-700 ${active ? 'w-12 bg-emerald-400' : 'w-0 bg-transparent'}`} style={{ transitionDelay: `${parseFloat(r.delay) + 0.4}s` }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ChangeoutVisual({ active }) {
  const steps = [
    { num: '1', label: 'Select Machine', icon: Cog, delay: '0.2s' },
    { num: '2', label: 'Pick Products', icon: Wrench, delay: '0.5s' },
    { num: '3', label: 'Enter Hours', icon: Activity, delay: '0.8s' },
    { num: '4', label: 'Upload Proof', icon: Camera, delay: '1.1s' },
  ];
  return (
    <div className="h-full flex items-center justify-center">
      <div className="grid grid-cols-2 gap-3 max-w-[280px]">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className={`bg-white rounded-xl p-3 shadow-sm border border-slate-100 text-center transition-all duration-500 ${active ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: s.delay }}>
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center mx-auto mb-2">
                <Icon size={16} className="text-slate-600" />
              </div>
              <div className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center mx-auto mb-1">{s.num}</div>
              <p className="text-[11px] font-medium text-slate-700">{s.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AnalyticsVisual({ active }) {
  const bars = [35, 65, 45, 80, 55, 70, 90];
  return (
    <div className="h-full flex flex-col items-center justify-center gap-4">
      <div className="flex items-end gap-2 h-24">
        {bars.map((h, i) => (
          <div key={i} className={`w-6 rounded-t-md transition-all duration-700 ${active ? 'bg-slate-900' : 'bg-slate-200'}`} style={{ height: active ? `${h}%` : '10%', transitionDelay: `${i * 0.1}s` }} />
        ))}
      </div>
      <div className="flex gap-2">
        {[
          { icon: DollarSign, label: 'Spend', color: 'text-emerald-500', delay: '0.5s' },
          { icon: TrendingDown, label: 'Savings', color: 'text-amber-500', delay: '0.7s' },
          { icon: Activity, label: 'Fleet', color: 'text-blue-500', delay: '0.9s' },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} className={`flex items-center gap-1 bg-white rounded-full px-3 py-1.5 shadow-sm border text-xs transition-all duration-500 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: item.delay }}>
              <Icon size={12} className={item.color} />
              <span className="font-medium text-slate-600">{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FinishVisual({ active }) {
  return (
    <div className="h-full flex items-center justify-center">
      <div className={`text-center transition-all duration-700 ${active ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
        <div className={`w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 transition-all duration-500 ${active ? 'ring-8 ring-emerald-50' : ''}`}>
          <CheckCircle size={36} className="text-emerald-600" />
        </div>
        <p className="text-base font-bold text-slate-900">All Set!</p>
        <p className="text-xs text-slate-400 mt-1">Your intelligence hub is ready</p>
      </div>
    </div>
  );
}

const VISUALS = { welcome: WelcomeVisual, setup: SetupVisual, team: TeamVisual, changeout: ChangeoutVisual, analytics: AnalyticsVisual, finish: FinishVisual };

export default function OnboardingWizard({ isModal = false, onClose }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [stepProgress, setStepProgress] = useState(0);
  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;
  const Visual = VISUALS[step.visual];

  useEffect(() => {
    api.get('/onboarding/status').then(res => {
      if (res.data?.completed_steps) setCompletedSteps(res.data.completed_steps);
    }).catch(() => {});
  }, []);

  // Auto-advance timer with progress
  useEffect(() => {
    setStepProgress(0);
    if (!isPlaying) return;
    const duration = step.duration;
    const interval = 50;
    let elapsed = 0;
    const timer = setInterval(() => {
      elapsed += interval;
      setStepProgress(Math.min((elapsed / duration) * 100, 100));
      if (elapsed >= duration) {
        clearInterval(timer);
        if (!isLast) {
          setCurrentStep(prev => prev + 1);
        } else {
          setIsPlaying(false);
        }
      }
    }, interval);
    return () => clearInterval(timer);
  }, [currentStep, isPlaying, step.duration, isLast]);

  const markStep = async (stepId) => {
    try {
      await api.post('/onboarding/complete', { step: stepId });
      setCompletedSteps(prev => prev.includes(stepId) ? prev : [...prev, stepId]);
    } catch (err) { console.warn('onboarding step save failed', err); }
  };

  const goToStep = (idx) => {
    markStep(step.id);
    setCurrentStep(idx);
    setIsPlaying(true);
  };

  const handleFinish = () => {
    markStep('finish');
    if (onClose) onClose();
    else navigate('/');
  };

  const handleDismiss = async () => {
    try { await api.post('/onboarding/complete', { dismissed: true }); } catch (err) { console.warn('onboarding dismiss failed', err); }
    if (onClose) onClose();
    else navigate('/');
  };

  const handleAction = () => {
    if (step.action) {
      markStep(step.id);
      if (onClose) onClose();
      navigate(step.action);
    }
  };

  const content = (
    <div className="flex flex-col h-full" data-testid="onboarding-wizard">
      {/* Visual area */}
      <div className="relative bg-gradient-to-br from-slate-50 to-slate-100 h-52 sm:h-60 flex-shrink-0 overflow-hidden rounded-t-lg">
        {Visual && <Visual active={true} />}
        {/* Play/Pause button */}
        <button onClick={() => setIsPlaying(!isPlaying)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm shadow-sm flex items-center justify-center hover:bg-white transition-colors" data-testid="play-pause-btn">
          {isPlaying ? <Pause size={14} className="text-slate-600" /> : <Play size={14} className="text-slate-600 ml-0.5" />}
        </button>
      </div>

      {/* Timeline scrubber */}
      <div className="flex items-center gap-1 px-5 pt-3 pb-1">
        {STEPS.map((s, i) => (
          <button key={s.id} onClick={() => goToStep(i)} className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer" data-testid={`timeline-step-${i}`}>
            <div className={`h-full rounded-full transition-all duration-300 ${i < currentStep ? 'bg-slate-900 w-full' : i === currentStep ? 'bg-slate-900' : 'w-0'}`} style={i === currentStep ? { width: `${stepProgress}%` } : {}} />
          </button>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 text-center mb-2">{currentStep + 1} / {STEPS.length}</p>

      {/* Content area */}
      <div className="px-5 pb-5 flex-1 flex flex-col">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center flex-shrink-0">
            {(() => { const Icon = step.icon; return <Icon size={18} className="text-white" />; })()}
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 leading-tight" data-testid="wizard-step-title">{step.title}</h2>
            <p className="text-xs text-slate-500">{step.subtitle}</p>
          </div>
        </div>

        {/* Highlights */}
        <div className="space-y-1.5 mb-4 flex-1">
          {step.highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle size={13} className="text-emerald-500 flex-shrink-0" />
              <span>{h}</span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1">
            {currentStep > 0 && (
              <Button variant="ghost" size="sm" onClick={() => goToStep(currentStep - 1)} className="text-xs text-slate-500 h-8 px-2" data-testid="wizard-back-btn">
                <ChevronLeft size={14} /> Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step.action && (
              <Button variant="outline" size="sm" onClick={handleAction} className="text-xs h-8" data-testid="wizard-action-btn">
                {step.actionLabel} <ArrowRight size={12} className="ml-1" />
              </Button>
            )}
            {!isLast ? (
              <>
                <Button variant="ghost" size="sm" onClick={handleDismiss} className="text-xs text-slate-400 h-8" data-testid="wizard-dismiss-btn">Skip</Button>
                <Button size="sm" onClick={() => goToStep(currentStep + 1)} className="bg-slate-900 hover:bg-slate-800 text-xs h-8" data-testid="wizard-next-btn">
                  Next <ChevronRight size={12} className="ml-0.5" />
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={handleFinish} className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8" data-testid="wizard-finish-btn">
                Launch Dashboard <ArrowRight size={12} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isModal) {
    return content;
  }

  // Full page mode (from /onboarding URL)
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-xl overflow-hidden border border-slate-200">
        {content}
      </div>
    </div>
  );
}
